import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 4;
export const ZOOM_STOPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

const clamp = ({ value, min, max }) => Math.min(max, Math.max(min, value));

const isNativeEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
    return true;
  }
  return target.isContentEditable;
};

export const nextZoomStop = ({ zoom, direction }) => {
  if (direction > 0) {
    return ZOOM_STOPS.find((stop) => stop > zoom + 0.001) || MAX_ZOOM;
  }
  return (
    [...ZOOM_STOPS].reverse().find((stop) => stop < zoom - 0.001) || MIN_ZOOM
  );
};

export const useCanvasZoom = ({
  viewportRef,
  boardRef,
  resetKey,
  lockRef,
}) => {
  const zoomRef = useRef(1);
  const pendingScrollRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  const applyZoom = useCallback(
    ({ nextZoom, originClientX, originClientY, fit = false }) => {
      const viewport = viewportRef.current;
      const current = zoomRef.current;
      const clamped = clamp({
        value: nextZoom,
        min: MIN_ZOOM,
        max: MAX_ZOOM,
      });
      if (Math.abs(clamped - current) < 0.001 && !fit) return;

      if (fit) {
        pendingScrollRef.current = null;
        zoomRef.current = 1;
        setZoom(1);
        if (viewport) {
          viewport.scrollLeft = 0;
          viewport.scrollTop = 0;
        }
        return;
      }

      if (viewport) {
        const rect = viewport.getBoundingClientRect();
        const overViewport =
          originClientX != null &&
          originClientY != null &&
          originClientX >= rect.left &&
          originClientX <= rect.right &&
          originClientY >= rect.top &&
          originClientY <= rect.bottom;
        const clientX = overViewport
          ? originClientX
          : rect.left + viewport.clientWidth / 2;
        const clientY = overViewport
          ? originClientY
          : rect.top + viewport.clientHeight / 2;
        const board = boardRef.current;
        const boardRect = board?.getBoundingClientRect();
        pendingScrollRef.current = {
          originClientX: clientX,
          originClientY: clientY,
          ratioX:
            boardRect && boardRect.width
              ? (clientX - boardRect.left) / boardRect.width
              : 0.5,
          ratioY:
            boardRect && boardRect.height
              ? (clientY - boardRect.top) / boardRect.height
              : 0.5,
        };
      }

      zoomRef.current = clamped;
      setZoom(clamped);
    },
    [boardRef, viewportRef]
  );

  const onZoomIn = useCallback(() => {
    applyZoom({
      nextZoom: nextZoomStop({ zoom: zoomRef.current, direction: 1 }),
    });
  }, [applyZoom]);

  const onZoomOut = useCallback(() => {
    applyZoom({
      nextZoom: nextZoomStop({ zoom: zoomRef.current, direction: -1 }),
    });
  }, [applyZoom]);

  const onFit = useCallback(() => {
    applyZoom({ nextZoom: 1, fit: true });
  }, [applyZoom]);

  useEffect(() => {
    zoomRef.current = 1;
    setZoom(1);
    pendingScrollRef.current = null;
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
      viewportRef.current.scrollTop = 0;
    }
  }, [resetKey, viewportRef]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    if (rect.width && rect.height) {
      setViewportSize({ width: rect.width, height: rect.height });
    }
  }, [viewportRef]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setViewportSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height }
      );
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [viewportRef]);

  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    const viewport = viewportRef.current;
    const board = boardRef.current;
    if (!pending || !viewport || !board) return;
    pendingScrollRef.current = null;
    const boardRect = board.getBoundingClientRect();
    const pointClientX = boardRect.left + pending.ratioX * boardRect.width;
    const pointClientY = boardRect.top + pending.ratioY * boardRect.height;
    viewport.scrollLeft += pending.originClientX - pointClientX;
    viewport.scrollTop += pending.originClientY - pointClientY;
  }, [boardRef, viewportRef, viewportSize.height, viewportSize.width, zoom]);

  useEffect(() => {
    const onWheel = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      if (lockRef?.current) return;
      const nextZoom = zoomRef.current * Math.exp(-event.deltaY * 0.0015);
      applyZoom({
        nextZoom,
        originClientX: event.clientX,
        originClientY: event.clientY,
      });
    };
    window.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => window.removeEventListener("wheel", onWheel, { capture: true });
  }, [applyZoom, lockRef]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isNativeEditableTarget(event.target)) return;
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier) return;
      const key = event.key;
      const code = event.code;
      if (key === "+" || key === "=" || code === "Equal" || code === "NumpadAdd") {
        event.preventDefault();
        onZoomIn();
        return;
      }
      if (
        key === "-" ||
        key === "_" ||
        code === "Minus" ||
        code === "NumpadSubtract"
      ) {
        event.preventDefault();
        onZoomOut();
        return;
      }
      if (key === "0" || code === "Digit0" || code === "Numpad0") {
        event.preventDefault();
        onFit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onFit, onZoomIn, onZoomOut]);

  return {
    zoom,
    viewportSize,
    onZoomIn,
    onZoomOut,
    onFit,
  };
};
